/* eslint-disable react/jsx-no-literals, no-console */
import type { Table } from "@tanstack/react-table";
import React, { useState } from "react";
import { TextInput } from "web-ui/juicy";
import { LegacyButton as Button } from "web-ui/legacy";

import { aiCompletionsBackend } from "../api/aiCompletionsBackend";
import styles from "../styles/AiTtsSearch.module.scss";
import {
  formatDisplayText,
  getDisplayLanguageName,
} from "../util/languageUtils";

import type { EnhancedVoiceListVoice } from "./VoiceFinder";

interface AiTtsSearchProps {
  reactTable: Table<EnhancedVoiceListVoice>;
  columnOptions: Record<string, string[]>;
  data: EnhancedVoiceListVoice[];
}

// Define the expected response format from the AI
interface AiFilterSuggestion {
  recommendation: string;
  filters?: {
    columnId: string;
    values: string[];
  }[];
}

// Column ID mapping to handle AI responses with different naming conventions
const COLUMN_ID_MAPPING: Record<string, string> = {
  character: "character",
  character_id: "character",
  gender: "gender",
  gender_id: "gender",
  in_use: "isUsedInLearningApp",
  language: "languageId",
  language_id: "languageId",
  provider: "provider",
  provider_id: "provider",
  voice: "name",
  voice_name: "name",
};

const AiTtsSearch = ({ reactTable, columnOptions }: AiTtsSearchProps) => {
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);

  // Generate a system prompt that includes column information
  const generateSystemPrompt = () => {
    // Get column information with sample values
    const columnInfo = Object.entries(columnOptions)
      .map(([columnId, values]) => {
        const displayName = columnId.startsWith("dynamic_")
          ? columnId.replace("dynamic_", "")
          : columnId;

        // Include a sample of values (up to 10)
        const sampleValues = values.slice(0, 10).join(", ");
        return `- ${displayName}: [${sampleValues}]`;
      })
      .join("\n");

    return `You are a helpful assistant for Duolingo TTS (Text-to-Speech) voice selection.
You can recommend voices based on specific criteria.

The voice data has the following columns and sample values:
${columnInfo}

When responding to a query about voice recommendations, format your response as JSON in this structure:
{
  "recommendation": "Your human-readable recommendation text here",
  "filters": [
    {
      "columnId": "column identifier matching one in the data",
      "values": ["value1", "value2"]
    }
  ]
}

For language recommendations, use the formal language name in your text but use the language ID in filters.
For example, use "Spanish" in your recommendation but "es" in the filter values.

Important rules:
1. ALWAYS match your filter values exactly to values in the data
2. Only include filters that are directly relevant to the user's query
3. For queries unrelated to voice selection, don't include filters
4. Provide clear, helpful recommendations based on the user's needs
5. Provide the response in valid JSON format that can be parsed

Column ID mapping for filters:
- For language: use "languageId" (not language_id)
- For voice name: use "name" (not voice_name)
- For gender: use "gender"
- For provider: use "provider"
- For character: use "character"
- For in use: use "isUsedInLearningApp"

For example, if the user asks for "female voices in Spanish", your response should be:
{
  "recommendation": "Here are some female TTS voices in Spanish.",
  "filters": [
    {
      "columnId": "languageId",
      "values": ["es"]
    },
    {
      "columnId": "gender",
      "values": ["FEMALE"]
    }
  ]
}`;
  };

  const handleSearch = async () => {
    if (!query) {
      return;
    }

    setIsLoading(true);
    setError("");
    setAppliedFilters([]);

    try {
      const result = await aiCompletionsBackend.getChatCompletion({
        systemPrompt: generateSystemPrompt(),
        useJsonMode: true,
        userPrompt: query,
      });

      // Parse the response
      try {
        const parsedResponse = JSON.parse(result.content) as AiFilterSuggestion;
        console.log("AI response:", parsedResponse);

        if (parsedResponse?.recommendation) {
          // Format the recommendation text to convert language codes to display names
          const formattedRecommendation = formatDisplayText(
            parsedResponse.recommendation,
          );
          setResponse(formattedRecommendation);

          // Apply filters if provided
          if (parsedResponse.filters && parsedResponse.filters.length > 0) {
            const filtersApplied: string[] = [];

            // Clear existing filters first
            reactTable.resetColumnFilters();

            // Apply each filter
            parsedResponse.filters.forEach(filter => {
              const { columnId, values } = filter;

              // Skip if column or values are invalid
              if (!columnId || !values || values.length === 0) {
                return;
              }

              // Map the column ID if needed
              let mappedColumnId = columnId;
              if (COLUMN_ID_MAPPING[columnId]) {
                mappedColumnId = COLUMN_ID_MAPPING[columnId];
              }

              // Check if column exists
              const column = reactTable.getColumn(mappedColumnId);
              if (!column) {
                return;
              }

              // For languageId, convert language codes to display names
              if (mappedColumnId === "languageId") {
                // Convert language codes to their display names for filtering
                const displayLanguages = values.map(code =>
                  getDisplayLanguageName(code),
                );

                // Apply the filter with display names instead of codes
                if (displayLanguages.length === 1) {
                  // Single-value case
                  column.setFilterValue(displayLanguages[0]);
                } else {
                  // Multi-value case
                  column.setFilterValue(displayLanguages);
                }

                // Add to applied filters with display names
                filtersApplied.push(`Language: ${displayLanguages.join(", ")}`);
              } else {
                // For other filters, use normal approach
                column.setFilterValue(values);

                // Track which filters were applied
                const displayName = mappedColumnId.startsWith("dynamic_")
                  ? mappedColumnId.replace("dynamic_", "")
                  : mappedColumnId;

                filtersApplied.push(`${displayName}: ${values.join(", ")}`);
              }
            });

            setAppliedFilters(filtersApplied);
          }
        } else {
          // Handle case where response doesn't have expected structure
          setResponse(result.content);
        }
      } catch (parseError: unknown) {
        console.error("Failed to parse AI response as JSON:", parseError);
        // Fall back to displaying the raw response
        setResponse(result.content);
      }
    } catch (err: unknown) {
      console.error("Error fetching AI completion:", err);
      setError("Failed to get response. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const clearFilters = () => {
    reactTable.resetColumnFilters();
    setAppliedFilters([]);
  };

  return (
    <div className={styles.container}>
      <h2>TTS AI Assistant</h2>
      <div className={styles.searchBox}>
        <TextInput
          className={styles.input}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about voice recommendations (e.g., 'Find me female Spanish voices')"
          type="text"
          value={query}
        />
        <Button
          color="macaw"
          disabled={isLoading || !query}
          onClick={handleSearch}
        >
          {isLoading ? "Loading..." : "Search"}
        </Button>
      </div>

      {(error || response) && (
        <div className={styles.response}>
          {error ? (
            <div className={styles.error}>{error}</div>
          ) : (
            <>
              <div className={styles.responseContent}>{response}</div>

              {appliedFilters.length > 0 && (
                <div className={styles.appliedFilters}>
                  <Button color="macaw" onClick={clearFilters} variant="stroke">
                    Clear Filters
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AiTtsSearch;
